/**
 * Topbar — sticky header with breadcrumbs, search, and the icon-actions
 * row. Search is read-only / not wired in MVP per
 * `docs/frontend/035-frontend-app-shell-plan.md §3` (the design's ⌘K
 * command palette is deferred to a follow-up task).
 */

import { useLocation } from 'react-router-dom';
import { useMeContext } from '../../context/MeContextProvider';
import { useClientScope } from '../../context/ClientScopeProvider';
import { hasAnyPermission, isClientOnly, PERM } from '../../permissions';
import { HelpIcon, SearchIcon } from './icons';
import { NotificationsBell } from './NotificationsBell';
import { UserMenu } from './UserMenu';

interface Crumb {
  readonly label: string;
}

function buildCrumbs(
  pathname: string,
  activeClientName: string | null,
  clientOnly: boolean,
): ReadonlyArray<Crumb> {
  const parts = pathname.split('/').filter((p) => p.length > 0);
  if (parts.length === 0 || parts[0] !== 'app') {
    return [{ label: 'Super agency' }];
  }
  const out: Crumb[] = [{ label: 'Super agency' }];
  if (parts.length === 1) return out;

  // /app/X
  if (parts[1] === 'clients') {
    out.push({ label: 'العملاء' });
    if (parts.length >= 3 && activeClientName) {
      out.push({ label: activeClientName });
      const tab = parts[3];
      if (tab) out.push({ label: tabLabel(tab) });
    }
    return out;
  }
  if (parts[1] === 'notifications') return [...out, { label: 'الإشعارات' }];
  if (parts[1] === 'settings') return [...out, { label: 'الإعدادات' }];
  if (parts[1] === 'team') {
    return [...out, { label: clientOnly ? 'فريق العميل' : 'الفريق والصلاحيات' }];
  }
  if (parts[1] === 'influencers') return [...out, { label: 'المؤثرون' }];
  if (parts[1] === 'crm') return [...out, { label: 'CRM' }];
  if (parts[1] === 'reports') return [...out, { label: 'التقارير' }];
  if (parts[1] === 'creative') return [...out, { label: 'مكتبة الإبداع' }];
  if (parts[1] === 'paid-media') return [...out, { label: 'الإعلانات المدفوعة' }];
  if (parts[1] === 'services') return [...out, { label: 'الخدمات والباقات' }];
  // Task 303-A-B — the continuity route. Without this the one page a client
  // of a non-operational agency can open renders under «Super agency» and
  // nothing else.
  if (parts[1] === 'contracts') return [...out, { label: 'العقد' }];
  return out;
}

function tabLabel(slug: string): string {
  switch (slug) {
    case 'overview':
      return 'نظرة عامة';
    case 'strategy':
      return 'الاستراتيجية';
    case 'content':
      return 'المحتوى';
    case 'publishing':
      return 'النشر';
    case 'paid-media':
      return 'الإعلانات المدفوعة';
    case 'creative':
      return 'مكتبة الإبداع';
    case 'creative-intelligence':
      // Task 223 — client-scoped Creative Intelligence breadcrumb leaf, so the
      // page shows under "… / <client> / ذكاء الإبداع" (not at agency root).
      return 'ذكاء الإبداع';
    case 'influencers':
      return 'المؤثرون';
    case 'reports':
      return 'التقارير';
    case 'crm':
      return 'علاقات العملاء';
    case 'services':
      return 'الخدمات';
    default:
      return slug;
  }
}

export function Topbar() {
  const location = useLocation();
  const { me } = useMeContext();
  const { clients, selectedClientId } = useClientScope();
  const activeClient = selectedClientId
    ? (clients.find((c) => c.id === selectedClientId) ?? null)
    : null;
  const clientOnly = me ? isClientOnly(me) : false;
  const crumbs = buildCrumbs(location.pathname, activeClient?.name ?? null, clientOnly);
  const last = crumbs[crumbs.length - 1];

  return (
    <header className="top">
      <div className="crumbs">
        {crumbs.slice(0, -1).map((c, i) => (
          <span key={`${c.label}-${i}`}>
            {c.label}
            <span className="sep" style={{ marginInline: 6 }}>
              /
            </span>
          </span>
        ))}
        {last ? <span className="here">{last.label}</span> : null}
      </div>
      <div className="search">
        <input type="text" placeholder="ابحث في العملاء، الحملات، الأصول…" disabled />
        <SearchIcon className="sicon" />
        <span className="kbd">⌘K</span>
      </div>
      <div className="top-actions">
        <button className="icon-btn" title="مساعدة" type="button">
          <HelpIcon className="" />
        </button>
        {me && !me.system_admin && hasAnyPermission(me.permissions, PERM.NOTIFICATION_READ) ? (
          <NotificationsBell />
        ) : null}
        {me ? <UserMenu /> : null}
      </div>
    </header>
  );
}
